// frontend/src/components/tickets/TicketResolutionAttemptsCard.jsx
//
// "Intentos de resolución automática" del ticket.
//
// Responde la pregunta operativa que antes no tenía respuesta: ¿por qué este
// ticket sigue abierto si el PSI actual ya está en rango? El backend registra
// cada evaluación con su evidencia (presión, temperatura, presión estimada en
// frío, rango de cierre) y su decisión; aquí solo se muestra.

import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import {
  Alert,
  Box,
  Chip,
  CircularProgress,
  Divider,
  Paper,
  Stack,
  Typography,
} from '@mui/material';
import { ThermostatAuto as ThermalIcon } from '@mui/icons-material';

import { api } from '../../services/api';
import { useUnitPreferences } from '../../hooks/useUnitPreferences';
import { formatPressureValue, formatTemperatureValue } from '../../utils/unitFormat';

const DECISION_META = {
  resolved: { label: 'Resuelto', color: 'success' },
  ignored_temperature_effect: { label: 'Bloqueado por temperatura', color: 'warning' },
  pending_confirmation: { label: 'Pendiente de confirmación', color: 'info' },
  rejected_no_recent_data: { label: 'Sin lectura reciente', color: 'default' },
};

const TicketResolutionAttemptsCard = ({ ticketId, formatTimestamp }) => {
  const { preferences } = useUnitPreferences();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!ticketId) return undefined;
    let cancelled = false;
    setLoading(true);
    api.alarmTickets.getResolutionAttempts(ticketId)
      .then((res) => { if (!cancelled) setData(res.data); })
      .catch(() => { if (!cancelled) setData(null); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [ticketId]);

  if (loading) {
    return (
      <Paper sx={{ p: 2 }}>
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
          <CircularProgress size={22} />
        </Box>
      </Paper>
    );
  }

  // Sin intentos registrados no se pinta la sección: no añade ruido.
  if (!data || !(data.attempts || []).length) return null;

  const stamp = (value) => (formatTimestamp ? formatTimestamp(value)
    : (value ? new Date(value).toLocaleString() : '—'));

  return (
    <Paper sx={{ p: 2 }}>
      <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }}>
        <ThermalIcon color="action" fontSize="small" />
        <Typography variant="subtitle1" fontWeight={700} sx={{ flexGrow: 1 }}>
          Intentos de resolución automática
        </Typography>
        <Chip size="small" variant="outlined" label={data.total} />
      </Stack>

      {data.blocked_by_temperature_count > 0 && (
        <Alert severity="warning" sx={{ mb: 1.5 }}>
          El sistema evitó {data.blocked_by_temperature_count} cierre(s) falso(s): la presión
          había vuelto al rango por aumento de temperatura, pero la presión estimada en frío
          seguía por debajo de lo permitido. Verificar con manómetro y calibrar en frío.
        </Alert>
      )}

      <Stack divider={<Divider flexItem />} spacing={1}>
        {data.attempts.map((attempt) => {
          const meta = DECISION_META[attempt.decision] || { label: attempt.decision_label, color: 'default' };
          return (
            <Box key={attempt.id} sx={{ pt: 0.5 }}>
              <Stack direction="row" alignItems="center" spacing={1} sx={{ flexWrap: 'wrap', gap: 0.5 }}>
                <Chip size="small" color={meta.color} label={meta.label} />
                <Typography variant="caption" color="text.secondary">
                  {stamp(attempt.evaluated_at)}
                </Typography>
                {attempt.blocked_by_temperature && (
                  <Chip size="small" variant="outlined" color="warning" label="Efecto de temperatura" />
                )}
              </Stack>

              <Stack direction="row" spacing={2} sx={{ mt: 0.5, flexWrap: 'wrap', gap: 1 }}>
                <Typography variant="body2">
                  <strong>PSI actual:</strong> {formatPressureValue(attempt.current_psi, preferences)}
                </Typography>
                <Typography variant="body2">
                  <strong>Temperatura:</strong> {formatTemperatureValue(attempt.current_temp, preferences)}
                </Typography>
                <Typography variant="body2">
                  <strong>Frío estimado:</strong> {formatPressureValue(attempt.estimated_cold_psi, preferences)}
                </Typography>
                {attempt.ideal_psi !== null && attempt.ideal_psi !== undefined && (
                  <Typography variant="body2">
                    <strong>Ideal frío:</strong> {formatPressureValue(attempt.ideal_psi, preferences)}
                  </Typography>
                )}
                {(attempt.close_range_min !== null || attempt.close_range_max !== null) && (
                  <Typography variant="body2">
                    <strong>Rango de cierre:</strong>{' '}
                    {formatPressureValue(attempt.close_range_min, preferences, { showUnit: false })}
                    {' – '}
                    {formatPressureValue(attempt.close_range_max, preferences)}
                  </Typography>
                )}
              </Stack>

              {attempt.reason && (
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                  {attempt.reason}
                </Typography>
              )}
            </Box>
          );
        })}
      </Stack>
    </Paper>
  );
};

TicketResolutionAttemptsCard.propTypes = {
  ticketId: PropTypes.string.isRequired,
  formatTimestamp: PropTypes.func,
};

export default TicketResolutionAttemptsCard;
